import { res, type Resources } from './structures.ts';
import { daysMs, hoursMs } from './units.ts';

/** Bastion geometry (SPEC §5). */
export const BASTION = {
  /** Interior radius: rampart ring sits at this distance from the keep. */
  radius: 7,
  pathMin: 10,
  pathMax: 14,
  /** Keep HP = 5000 × sqrt(total activity). */
  keepHpBase: 5000,
  /** Cells around the keep that stay free (plaza). */
  plazaRadius: 1,
};

/** Sectors by player count. */
export const sectorsForPlayers = (players: number): number => {
  if (players <= 2) return 2;
  if (players <= 4) return 3;
  if (players <= 6) return 4;
  return 5;
};

export const MIN_PLAYERS = 2;
export const MAX_PLAYERS = 8;

/**
 * Parties of five to eight in crypts, the saga, the cellars, the Furrows and incursions (DECISIONS 2026-10-29).
 * The party tables (nightmares, and bosses' health) were measured up to four heroes. Past that the fifth hero
 * adds `extraBp` of the average hero of four and every one after it `rampBp` more than the one before: a big party
 * covers itself more with every hero (revives, auras and heals that reach more of them, focus). Only `countBp` of
 * that comes as more nightmares, the rest makes every nightmare tougher: eight heroes don't double the bodies on
 * a phone's screen. A place can carry its own `big` (the cellars, against the rising ink).
 */
export const BIG_PARTY = { measured: 4, extraBp: 17000, rampBp: 8000, countBp: 2000 };
export interface BigPartyScale {
  extraBp: number;
  rampBp: number;
}

/** Activity weight by time since last session (SPEC §9). */
export const ACTIVITY_STEPS: { withinMs: number; weightBp: number }[] = [
  { withinMs: hoursMs(48), weightBp: 10000 },
  { withinMs: daysMs(4), weightBp: 5000 },
  { withinMs: daysMs(7), weightBp: 2000 },
];
export const MIN_TOTAL_ACTIVITY_BP = 10000;

/** Starting storage per player. */
export const START_STORAGE_PER_PLAYER: Resources = res({ wood: 120, stone: 120, crystal: 30 });
/** Dust per defended wave per participating player. */
export const WAVE_DUST_PER_PLAYER = 15;
/** Lost Marea: storage loses 30 %. */
export const LOSS_STORAGE_BP = 3000;
export const LOSSES_TO_END = 2;

/** Calendar (SPEC §8.1). */
export const CALENDAR = {
  mareaEveryDays: 4,
  firstMareaDay: 2,
  omenHoursBefore: 12,
  defaultWaveHours: [10, 18, 23] as [number, number, number],
  /** The three wave hours of a day go in order, at least this many hours apart (no overlapping adelantos). */
  minWaveGapHours: 2,
  /** "Adelanto" can be played this long before the wave. */
  previewWindowMs: hoursMs(2),
};

/**
 * Mercenaries, hired for the whole bastion until the end of the Marea: 80 wood + 40 stone ×
 * (4 / players, min 0.5). The best standing barracks' level is how many serve at once and how
 * hard they hit (share of their class's strength); they also get tougher with each Marea.
 */
export const MERCENARY = {
  cost: res({ wood: 80, stone: 40 }),
  minScaleBp: 5000,
  /** By barracks level 1, 2, 3. */
  slots: [1, 2, 3],
  powerBp: [6000, 6500, 7000],
  /** Hero level of a mercenary: base + per Marea. */
  level: 3,
  levelPerMarea: 1,
};

/** Incursions (SPEC §13). */
export const INCURSION = {
  nodesPerDay: 8,
  extractTicks: 180 * 12,
  waves: 3,
  budgetBase: 47,
  /** 12 % until the class balance (docs/BALANCE_CLASES.md): skills grow with the level; 15 % keeps T1 as hard (balance:mirror). */
  budgetLevelBp: 1500,
  maxTurrets: 3,
  reward: 120,
  rewardDust: 38,
  rewardXp: 100,
  fullPerDay: 3,
  reducedBp: 2500,
  carryShareBp: 1000,
  /** Extraction speed while no nightmare is on the field. */
  quietRate: 3,
  /** Portable turrets: a call to make, not a free win — two, 45 s each, and nightmares can break them. */
  turrets: { max: 2, ticks: 45 * 12, hp: 220, damage: 20 },
  /** A closing elite is the incursion's boss: ×2 health on top of the elite's ×2. */
  eliteHpBp: 14000,
  /** Up to 7 companions: friends' ghosts or friends playing live. */
  maxCompanions: 7,
  /**
   * The budget and toughness below were measured up to 3 companions. Each one past that (live first) brings a
   * hero's worth of a party of four by `big` (a ghost by ghostBudgetBp / liveBudgetBp), split by BIG_PARTY.
   * Gentler than a crypt's, like the saga's: the node falls before the heroes do.
   */
  measuredCompanions: 3,
  big: { extraBp: 14500, rampBp: 6000 },
  /** More nightmares per companion: a ghost (70 % strength) or a live hero. */
  ghostBudgetBp: 6500,
  liveBudgetBp: 8500,
  /** Every nightmare in an incursion is this much tougher than in a wave (bp). */
  /** 125 % until the class balance (docs/BALANCE_CLASES.md): 130 % with the level growth above (balance:mirror). */
  hpBp: 13000,
  /** A party makes every nightmare tougher too, not only more of them (bp per companion). */
  ghostHpBp: 2700,
  liveHpBp: 3200,
  /**
   * What each live friend's hero brings back from the node, on top of the leader's share (into the storage). A
   * whole share, and it spends one of their runs of the day: going together never pays less than going apart.
   */
  liveRewardBp: 10000,
};
export type NodeKind = 'quarry' | 'forest' | 'crystal' | 'altar' | 'rescue' | 'lair' | 'iron' | 'fort';

/**
 * Each kind of place plays its own way (quarry is the plain assault):
 * - forest: part of every wave comes out of the woods at the edge of the clearing, not down the paths;
 * - crystal: the crystals call the nightmares, they run for the node and a hero can't hold them (taunts still do);
 * - altar: a corrupted altar raises an elite at the end of the last wave;
 * - rescue: the caged golem's cries bring each wave in a few tight packs, a short breather between them.
 */
export const INCURSION_TWISTS = {
  forest: { ambushBp: 3500 },
  crystal: { rush: true, budgetBp: 8000 },
  altar: { finalElite: true },
  rescue: { spacing: 3, packs: 3, packGap: 96, budgetBp: 8500 },
  /** A warlord's den: two more elites close the last wave; a rare piece of gear on a win. */
  lair: { finalElites: 2 },
  /** An iron mine: a plain assault, deep in the shell. */
  iron: {},
  /** The sappers' fort: its sappers dig at you; beat them and the next wave has none. */
  fort: {},
} as const;

/**
 * What an incursion asks of the hero (docs/MISIONES.md): hold the node while it is extracted, break a
 * nest (raid, to come) or carry the old garrison's caches to the cart (plunder). Each run draws its own.
 */
export type IncursionMission = 'extract' | 'raid' | 'plunder';
/** The missions that can come up (raid joins once it is built). */
export const INCURSION_MISSIONS: IncursionMission[] = ['extract', 'plunder'];
/** Below this group level every incursion is an extraction: the first days teach it. */
export const MISSIONS_FROM_GROUP = 5;
/**
 * How likely each mission is at each kind of place. Extraction can always come up; places that are not
 * here (lost golem, warlord's den, iron mine, sappers' fort) keep their own task.
 */
export const MISSION_WEIGHTS: Partial<Record<NodeKind, Partial<Record<IncursionMission, number>>>> = {
  forest: { extract: 3, raid: 2 },
  altar: { extract: 3, raid: 2 },
  quarry: { extract: 3, plunder: 2 },
  crystal: { extract: 3, plunder: 2 },
};

/**
 * Plunder (docs/MISIONES.md): the old garrison's caches lie across the clearing. A hero carries a chest
 * to the cart in the middle (slow, no blows; skills still work); a coffer takes two and is worth far
 * more, so it only shows up with two or more heroes playing live. Draggers haul what lies on the ground
 * back to their holes. Waves come every `waveEvery`, each bigger than the last; the group leaves when it
 * wants (every hero still standing by the cart), or the golem leaves on its own at `maxTicks`.
 */
export const PLUNDER = {
  chests: 26,
  coffers: 2,
  chestValue: 1,
  cofferValue: 6,
  /** Loads worth one plain extraction; the pay never goes past `capBp` of that. */
  fullLoad: 27,
  capBp: 15000,
  /** Leaving with less than this is not a win (the place doesn't count as taken), though the load is paid. */
  winLoad: 8,
  /** Everyone down at once, or the cart broken: this share of the load (bp). */
  fallenBp: 5000,
  firstWave: 20 * 12,
  waveEvery: 40 * 12,
  /** Each wave's budget over the one before (bp), on a first wave of `waveBudgetBp` of an extraction wave. */
  waveGrowBp: 3500,
  waveBudgetBp: 3700,
  /**
   * Its nightmares' health (bp, on top of the place's level). 115 % since the class balance (docs/BALANCE_CLASES.md), on top of
   * the incursions' own raise: a plunder must not get easier (plunder-2p: bots 58.5 % against 63.5 %).
   */
  hpBp: 11500,
  /** The golem leaves by itself here, with what is loaded (a replay can't run past ~5.5 minutes). */
  maxTicks: 280 * 12,
  /** From this wave on (1 is the first) every cache loaded is worth `lateBonus` more: staying pays, and costs. */
  lateWave: 3,
  lateBonus: 4,
  /** Draggers in each wave: `base`, and one more every `per` waves. */
  draggers: { base: 1, per: 3 },
  /** A dragger hauling a chest moves at this share of its pace (bp). */
  dragSpeedBp: 6000,
  /** A chest put down this close to the cart is loaded. */
  cartRange: 1500,
  /** Within this of the cart a hero counts as at the cart (to leave). */
  leaveRange: 2600,
  /** Reach to pick up a chest. */
  reach: 1300,
  /** A carrier's pace (bp of the hero's): a chest, and a coffer between two (it keeps them close). */
  chestSpeedBp: 3000,
  cofferSpeedBp: 2800,
  cofferLeash: 900,
  /** `nearChests` of the chests lie in the clearing's outer rings (from `minRing`); the rest out on the roads (never on a hole), the coffers past `cofferOut` rings beyond the clearing. */
  nearChests: 7,
  minRing: 3,
  cofferOut: 3,
};

/**
 * The titan seen from above (SPEC §13): 18 places in three rings around the bastion. Ring 1 is the
 * shell, open from day one; winning an incursion opens its neighbors for the whole group. Coordinates
 * are 0..1000 on the map; kinds are the lay of the land and never change.
 */
export interface IncursionSite {
  id: number;
  ring: 1 | 2 | 3;
  x: number;
  y: number;
  kind: Exclude<NodeKind, 'rescue'>;
  next: number[];
  /** Opens by itself in this era (the iron mines), not by exploring. */
  minEra?: number;
}
export const INCURSION_SITES: IncursionSite[] = [
  { id: 0, ring: 1, x: 500, y: 380, kind: 'quarry', next: [1, 4, 5] },
  { id: 1, ring: 1, x: 643, y: 484, kind: 'forest', next: [0, 2, 6, 7] },
  { id: 2, ring: 1, x: 588, y: 651, kind: 'crystal', next: [1, 3, 7, 8] },
  { id: 3, ring: 1, x: 412, y: 651, kind: 'quarry', next: [2, 4, 9, 10] },
  { id: 4, ring: 1, x: 357, y: 484, kind: 'forest', next: [0, 3, 10, 11] },
  { id: 5, ring: 2, x: 500, y: 245, kind: 'altar', next: [0, 6, 11, 12] },
  { id: 6, ring: 2, x: 725, y: 355, kind: 'quarry', next: [1, 5, 7, 13] },
  { id: 7, ring: 2, x: 771, y: 618, kind: 'forest', next: [1, 2, 6, 8, 14] },
  { id: 8, ring: 2, x: 634, y: 782, kind: 'crystal', next: [2, 7, 9, 14, 15] },
  { id: 9, ring: 2, x: 366, y: 782, kind: 'quarry', next: [3, 8, 10, 15, 16] },
  { id: 10, ring: 2, x: 229, y: 618, kind: 'forest', next: [3, 4, 9, 11, 16] },
  { id: 11, ring: 2, x: 275, y: 355, kind: 'crystal', next: [4, 5, 10, 17] },
  { id: 12, ring: 3, x: 500, y: 90, kind: 'lair', next: [5] },
  { id: 13, ring: 3, x: 856, y: 307, kind: 'altar', next: [6] },
  { id: 14, ring: 3, x: 848, y: 765, kind: 'crystal', next: [7, 8] },
  { id: 15, ring: 3, x: 500, y: 950, kind: 'lair', next: [8, 9] },
  { id: 16, ring: 3, x: 152, y: 765, kind: 'altar', next: [9, 10] },
  { id: 17, ring: 3, x: 144, y: 307, kind: 'crystal', next: [11] },
  // The iron mines: veins at the far east and west of the shell, open from era II.
  { id: 18, ring: 3, x: 935, y: 540, kind: 'iron', next: [], minEra: 2 },
  { id: 19, ring: 3, x: 65, y: 540, kind: 'iron', next: [], minEra: 2 },
  // The sappers' fort: win it and the next wave comes without sappers.
  { id: 20, ring: 3, x: 790, y: 130, kind: 'fort', next: [], minEra: 2 },
];

/** Farther from the bastion: more and tougher nightmares, better loot. Ring 3 opens at group level 5. */
export const INCURSION_RINGS: Record<1 | 2 | 3, { budgetBp: number; rewardBp: number; itemBp: number; inkBp: number; finalElites: number; elitePerWave: number; minGroup: number }> = {
  1: { budgetBp: 10000, rewardBp: 10000, itemBp: 2500, inkBp: 0, finalElites: 0, elitePerWave: 0, minGroup: 1 },
  2: { budgetBp: 8500, rewardBp: 11500, itemBp: 4000, inkBp: 1000, finalElites: 1, elitePerWave: 0, minGroup: 1 },
  3: { budgetBp: 8500, rewardBp: 22000, itemBp: 10000, inkBp: 2000, finalElites: 0, elitePerWave: 1, minGroup: 5 },
};
/**
 * Incursion levels (docs/NIVELES.md). Level I is the plain incursion. Level II, Ascuas, opens at group
 * level 5 at every place the group has won at level I: lava cracks that burn, eruptions to dodge,
 * the magma lord closing the last wave, and embers for the forge. Level III comes at group level 8.
 */
export const INCURSION_TIERS = {
  2: {
    minGroup: 5,
    /**
     * As many nightmares as level I, no tougher: the lava, the eruptions and the lord already make it far
     * harder (measured with Wardens' heroes as ghosts: 52 % won at level I, 10 % alone and 38 % with two
     * companions here; live players dodge better). Raise these once real runs say so.
     * Health 95 % since the class balance (docs/BALANCE_CLASES.md): with the incursions' level growth raised, 95 % keeps it
     * at least as hard as before (balance:mirror, bots 62 % against 64 %).
     */
    budgetBp: 10000,
    hpBp: 9500,
    /** Resources and XP. */
    rewardBp: 15000,
    /** Lava cracks on the ground, off the paths: how many, how wide, what they burn per second. */
    cracks: 5,
    crackRadius: 750,
    crackDamage: 9,
    /** An eruption is marked this long before it bursts, this wide, and hits this hard. */
    eruptionWarn: 24,
    eruptionRadius: 1100,
    eruptionDamage: 30,
    /** One eruption near a hero every this many ticks (faster in the last wave). */
    eruptionEvery: 9 * 12,
    eruptionEveryLast: 5 * 12,
    /** Embers for the leader (and each live friend) on a win; a share of it for a partial run. */
    embers: 3,
    /** A sure piece of gear, uncommon or better; the lord's own piece is rare this often. */
    bossRareBp: 5000,
  },
  /** Obsidiana (docs/NIVELES.md): glass that cracks under whoever stands still, shards where inked nightmares fall, the colossus. */
  3: {
    minGroup: 8,
    /**
     * Real heroes won 94 % at +10 % (level 14 on average); +45 % brought the same fights to about 57 % (balance:mirror),
     * which later runs put at 73 %. +60 % since 2026-10-29, also for the new gear slots (docs/EQUIPO.md): 55 % today, 66 % with them full.
     * Health 250 % since the class balance (skills grow with the level, the warrior cleaves): at least as hard as before for people (balance:mirror, bots 27 %).
     */
    budgetBp: 16000,
    hpBp: 25000,
    /** From `UNIQUE_EASE.fromLevel` (the hero who leads it): without the early armour and amulet, back to what people won (63 → 64 %). */
    easedHpBp: 18000,
    rewardBp: 20000,
    /** Standing still this long on the glass cracks it; it gives way this much later, cuts this hard, and the pit lasts this long. */
    glassStill: 24,
    glassWarn: 18,
    glassRadius: 650,
    glassDamage: 28,
    pitTicks: 5 * 12,
    pitDamage: 6,
    /** No glass cracks this close to a guard post or the node. */
    postClear: 1500,
    /** Shards an inked nightmare leaves when it falls: how wide, how long, what they cut every half second. */
    shardsRadius: 550,
    shardsTicks: 4 * 12,
    shardsDamage: 5,
    /** Obsidian shards for the leader (and each live friend) on a win. */
    shards: 3,
    /** A sure piece, uncommon or better; the colossus's own piece is rare this often. */
    bossRareBp: 7500,
  },
} as const;

/** The magma lord: every few seconds it slams the ground and eruptions rise under the heroes. */
export const MAGMA_LORD = { slamEvery: 9 * 12, slamEruptions: 3 };

/**
 * The obsidian colossus: every `every` ticks it shines for `shineTicks`, turning `reflectBp` of every blow back
 * on whoever struck it (heroes should stop hitting); when the shine ends it slams, leaving a ring of shards.
 */
export const COLOSSUS = { every: 8 * 12, shineTicks: 3 * 12, warnTicks: 12, reflectBp: 5000, slamRadius: 2200, slamDamage: 34, ringShards: 6, ringRadius: 2000 };

/** Carving at the forge: a rare piece's effect becomes another one, drawn at random. */
export const CARVE = { shards: 4, dust: 100 };

/** Tempering at the forge: one rarity step up (common → uncommon → rare), keeping what the piece has. */
export const TEMPER = {
  uncommon: { embers: 3, dust: 60 },
  rare: { embers: 6, dust: 140 },
};

/**
 * Offerings at the keep: the group burns dream dust from the storage for a blessing that lasts the rest of
 * the Marea (every wave and every sector). One of each kind per Marea; the price grows with the Marea
 * (`dust` × (Marea + 1)). Unlike the laboratory's elixirs (the heroes, one wave), they bless the bastion:
 * - oil: towers hit `towerDamageBp` harder;
 * - mortar: walls, gates and towers take `structureTakenBp` of the damage;
 * - vigil: the spectres fight with `ghostPowerBp` more power.
 */
export type OfferingKind = 'oil' | 'mortar' | 'vigil';
export const OFFERING_KINDS: OfferingKind[] = ['oil', 'mortar', 'vigil'];
export const OFFERINGS = {
  dust: 1000,
  oil: { towerDamageBp: 1000 },
  mortar: { structureTakenBp: 8500 },
  vigil: { ghostPowerBp: 1500 },
};

/** Ticks between two spawns of a wave (the rescue packs them tighter). */
export const INCURSION_SPAWN_SPACING = 14;

/** The day's omen: one for every incursion of a day, the same for the whole group. */
export type IncursionOmen = 'fog' | 'frenzy' | 'ink' | 'swarm' | 'storm' | 'frost' | 'bloodMoon' | 'starfall';

/** Lightning (storm): a strike marked on the ground near a hero or a nightmare, that hits everyone under it. */
export interface OmenBolts {
  /** One strike every this many ticks while nightmares stand, marked this long before it falls. */
  every: number;
  warn: number;
  radius: number;
  heroDamage: number;
  enemyDamage: number;
  /** One strike in this many goes near a nightmare instead of a hero (bp): lure them under it. */
  onEnemyBp: number;
}

/** Falling stars (starfall): one lands near a hero now and then; the first hero to reach it is empowered. */
export interface OmenStars {
  every: number;
  /** Its light shows where it will land this long before; then it lies there this long. */
  warn: number;
  ticks: number;
  radius: number;
  /** How far from the hero it falls (a few steps away: go and get it). */
  minDist: number;
  maxDist: number;
  /** Landing, it burns the nightmares right under it this much. */
  impactDamage: number;
  /** Picking it up: damage ×dmgBp for buffTicks, and back this share of health. */
  dmgBp: number;
  buffTicks: number;
  healBp: number;
}

export interface OmenDef {
  heroRangeBp?: number;
  enemySpeedBp?: number;
  inkBp?: number;
  budgetBp?: number;
  enemyHpBp?: number;
  /** Nightmares hit this much harder (bp). */
  enemyDamageBp?: number;
  /** Heroes' basic attacks heal this share of the damage they deal, on top of their gear. */
  heroLifestealBp?: number;
  bolts?: OmenBolts;
  stars?: OmenStars;
}

export const INCURSION_OMENS: Record<IncursionOmen, OmenDef> = {
  /** Shorter sight: hero attack range −20 %. */
  fog: { heroRangeBp: 8000 },
  /** Nightmares 25 % faster. */
  frenzy: { enemySpeedBp: 12500 },
  /** 35 % of the nightmares come inked (tier 2), even in light sleep. */
  ink: { inkBp: 3500 },
  /** 50 % more nightmares, each with 30 % less health. */
  swarm: { budgetBp: 15000, enemyHpBp: 7000 },
  /** Lightning falls on the field every few seconds, on heroes and nightmares alike: dodge it, or lead them under it. */
  storm: { bolts: { every: 5 * 12, warn: 20, radius: 1100, heroDamage: 50, enemyDamage: 35, onEnemyBp: 3000 } },
  /** Hard frost: nightmares 20 % slower, but 15 % tougher. */
  frost: { enemySpeedBp: 8000, enemyHpBp: 11500 },
  /** Blood moon: nightmares hit 30 % harder, but every hero's blow heals 12 % of what it deals. */
  bloodMoon: { enemyDamageBp: 13000, heroLifestealBp: 1200 },
  /** Falling stars: one lands near the heroes every 14 s and empowers whoever reaches it; 15 % more nightmares. */
  starfall: { budgetBp: 11500, stars: { every: 14 * 12, warn: 24, ticks: 10 * 12, radius: 750, minDist: 1600, maxDist: 3200, impactDamage: 60, dmgBp: 14000, buffTicks: 8 * 12, healBp: 2500 } },
};
export const INCURSION_OMEN_LIST: IncursionOmen[] = ['fog', 'frenzy', 'ink', 'swarm', 'storm', 'frost', 'bloodMoon', 'starfall'];
/**
 * The first four omens were the only ones before this instant: a day whose nominal start (the campaign's
 * creation plus that many days) comes before it still draws among them, so no running day changes its omen.
 */
export const OMENS_WIDE_FROM = 1_793_059_200_000; // 2026-10-27 00:00 UTC
export const OMENS_BEFORE = 4;

/** Lightning on the bastion's towers (storm, docs/PRESAGIOS.md §3.2): never on walls, gates, traps or the keep. */
export interface SkyBolts {
  /** One strike every this many ticks while nightmares stand, marked this long before it falls. */
  every: number;
  warn: number;
  /** For the eye only: the mark and the burn. */
  radius: number;
  /** Of the struck tower's max health (bp). */
  structureBp: number;
}

/**
 * What the day's omen does to a bastion wave (docs/PRESAGIOS.md): only the omens with an answer the group can
 * build, and only where it can be built. The rest leave waves as they are.
 */
export interface SkyDef {
  /** The shooting towers' reach (bp); a standing beacon within its range keeps a tower's whole reach. */
  towerRangeBp?: number;
  bolts?: SkyBolts;
  /** Only on terraced land from this era (where the lightning rod stands). */
  landEra?: number;
  /** The towers' reload (bp, slower), but those a charged burning-oil trap keeps warm. */
  towerReloadBp?: number;
  /** How near a burning-oil trap a tower stays warm (from each one's anchor cell). */
  warmRange?: number;
  /** A sky that came later changes waves only from this instant (past `BASTION_OMENS_FROM`). */
  from?: number;
}
export const BASTION_OMENS: Partial<Record<IncursionOmen, SkyDef>> = {
  fog: { towerRangeBp: 8000 },
  storm: { bolts: { every: 6 * 12, warn: 20, radius: 600, structureBp: 1000 }, landEra: 4 },
  // At least 12 h and a Marea's two days after it shipped, so no warning already given changes (docs/PRESAGIOS.md §3.3).
  frost: { towerReloadBp: 12500, warmRange: 2000, from: 1_794_096_000_000 }, // 2026-11-08 00:00 UTC
};
/** Waves scheduled before this instant play under a clear sky, so no omen already announced changes. */
export const BASTION_OMENS_FROM = 1_794_009_600_000; // 2026-11-07 00:00 UTC

/** Chosen before going in: a dangerous run brings more nightmares and an elite closing every wave, for more loot. */
export type IncursionRisk = 'normal' | 'danger';
export const INCURSION_DANGER = {
  budgetBp: 14000,
  /** One elite at the end of every wave. */
  elitePerWave: 1,
  /** Resources, dust and XP. */
  rewardBp: 17500,
  /** Chance of an item on a win (normal: 25 %). */
  itemBp: 5000,
};
export const INCURSION_ITEM_BP = 2500;

/** Session commit cadence. */
export const SESSION_COMMIT_EVERY_MS = 60_000;
